// ProgressBar — slim composition primitive (CHG-1236).
//
// Tiny addition to the composition pack established in CHG-1225, permitted by
// the v7 brief because the Campaigns Overview tab needs a visible progress
// indicator (raised vs goal). Stays slim (< 80 LOC) — generic enough that
// future entities (pledge fulfillment, recurring-donation lifetime cap,
// milestone progress) can reuse without modification.
//
// API: { value, max, tone?, label?, valueFormatted? }
//   value          numeric progress
//   max            total at which the bar is full (defaults to 100)
//   tone           tone token mapped to a tint class (defaults to g-giving)
//   label          optional left-side text
//   valueFormatted optional right-side text (typically a currency / percent
//                  string; falls back to "{pct}%")
// The bar caps visually at 100% but the right-side text shows the actual
// percentage — over-funded campaigns surface their over-funding without
// breaking the layout.

import type { CSSProperties } from "react";

const TONE_BAR_CLASS: Record<string, string> = {
  "g-home": "tint-home",
  "g-people": "tint-people",
  "g-giving": "tint-giving",
  "g-events": "tint-events",
  "g-channels": "tint-channels",
  "g-imports": "tint-imports",
  "g-automation": "tint-automation",
  "g-communications": "tint-communications",
  "g-settings": "tint-settings",
  "g-mygiving": "tint-mygiving",
};

export interface ProgressBarProps {
  value: number;
  max?: number;
  tone?: keyof typeof TONE_BAR_CLASS;
  label?: string;
  valueFormatted?: string;
  /** Visual height in pixels (default 8). */
  height?: number;
  /** Show numeric pct after the label (default true). */
  showPercent?: boolean;
}

export function ProgressBar({
  value,
  max = 100,
  tone = "g-giving",
  label,
  valueFormatted,
  height = 8,
  showPercent = true,
}: ProgressBarProps) {
  const denom = max > 0 ? max : 1;
  const ratio = value / denom;
  const cappedPct = Math.max(0, Math.min(ratio, 1)) * 100;
  const actualPct = Math.max(0, ratio * 100);
  const rightText = valueFormatted ?? (showPercent ? `${actualPct.toFixed(1)}%` : "");

  const barStyle: CSSProperties = {
    height,
    borderRadius: height / 2,
    background: "var(--color-surface-2)",
    overflow: "hidden",
    position: "relative",
  };
  const fillStyle: CSSProperties = {
    height: "100%",
    width: `${cappedPct}%`,
    transition: "width 240ms ease",
  };

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-valuetext={rightText}
      data-tone={tone}
    >
      {(label || rightText) && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 13,
            marginBottom: 6,
          }}
        >
          <span style={{ color: "var(--color-text)" }}>{label ?? ""}</span>
          <span className="tabular" style={{ color: "var(--color-text-muted)" }}>
            {rightText}
          </span>
        </div>
      )}
      <div style={barStyle}>
        <div className={TONE_BAR_CLASS[tone] ?? "tint-giving"} style={fillStyle} />
      </div>
    </div>
  );
}
